/**
 * Billing & Rechnungen — Self-Service-Center für Verkäufer und Admins.
 *
 * Funktionen:
 *  • aktuelles Abo-Detail (Plan, Status, nächste Abrechnung, „Wird gekündigt")
 *  • Liste der letzten Transaktionen (Einmalkäufe + Abo-Rechnungen)
 *  • Direkt-Download der Stripe-Rechnungs-PDFs
 *  • Button → Stripe Customer Portal (Zahlart, Rechnungsadresse, Kündigung)
 *  • Quicklink → Paywall ('Mehr Minuten kaufen')
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, CreditCard, Loader2, Receipt, ExternalLink, Download,
  Sparkles, AlertCircle, Timer, Plus,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { dateStyle: "medium" }); }
  catch (e) { return iso; }
};

const formatEUR = (n) => {
  if (n === null || n === undefined) return "—";
  try { return n.toLocaleString("de-DE", { style: "currency", currency: "EUR" }); }
  catch (e) { return `${n} €`; }
};

const statusBadge = (status) => {
  const map = {
    paid:   { label: "Bezahlt",       cls: "bg-novatus-green/15 text-novatus-green border-novatus-green/40" },
    open:   { label: "Offen",         cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    failed: { label: "Fehlgeschlagen",cls: "bg-red-500/15 text-red-400 border-red-500/40" },
    void:   { label: "Storniert",     cls: "bg-novatus-card-2 text-novatus-text-3 border-novatus" },
    uncollectible: { label: "Uneinbringlich", cls: "bg-red-500/15 text-red-400 border-red-500/40" },
    draft:  { label: "Entwurf",       cls: "bg-novatus-card-2 text-novatus-text-3 border-novatus" },
  };
  const meta = map[status] || { label: status || "?", cls: "bg-novatus-card-2 text-novatus-text-3 border-novatus" };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const BillingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [hasStripeLink, setHasStripeLink] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, invRes] = await Promise.all([
        api.get("/payments/my-subscription").catch(() => ({ data: null })),
        api.get("/payments/my-invoices?limit=20").catch(() => ({ data: { items: [], has_stripe_link: false } })),
      ]);
      setSubscription(subRes.data);
      setInvoices(invRes.data?.items || []);
      setHasStripeLink(!!invRes.data?.has_stripe_link);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/billing-portal", { return_url: window.location.href });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing-Portal konnte nicht geöffnet werden");
    } finally {
      setPortalLoading(false);
    }
  };

  const openPaywall = () => {
    try { window.dispatchEvent(new CustomEvent("open-paywall")); }
    catch (e) { navigate("/dashboard"); }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-novatus-text-3" data-testid="billing-loading">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Lade Rechnungen…
      </div>
    );
  }

  const activeSub = subscription?.active ? subscription.subscription : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="billing-page">
      <button
        onClick={() => navigate(-1)}
        className="hidden sm:inline-flex text-sm text-novatus-text-3 hover:text-novatus-green items-center gap-1 mb-4"
        data-testid="billing-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>

      <p className="label-eyebrow text-novatus-green">Rechnungswesen</p>
      <h1 className="font-display font-black text-3xl sm:text-5xl text-white mt-1 leading-tight">Billing & Rechnungen</h1>
      <p className="text-novatus-text-2 mt-3 max-w-2xl">
        Übersicht über deine Käufe, dein aktives Abo und alle Rechnungen. Verwalte
        deine Zahlart, Rechnungsadresse und USt-IdNr. direkt im Stripe-Portal.
      </p>

      {/* Active subscription */}
      {activeSub ? (
        <div className="nv-card mt-8 p-6 border-l-4 border-l-novatus-green animate-slide-up" data-testid="billing-active-sub">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
              <CreditCard className="w-6 h-6 text-novatus-green" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="label-eyebrow text-novatus-green">Aktives Abo</p>
              <h2 className="font-display font-bold text-white text-xl mt-1">
                {activeSub.plan_label} · {activeSub.minutes_per_month} Min/Monat
              </h2>
              <p className="text-sm text-novatus-text-2 mt-2">
                Status: <span className="text-white font-bold">{activeSub.status}</span>
                {activeSub.current_period_end && (
                  <> · Nächste Abrechnung: <span className="text-white font-bold">{new Date(activeSub.current_period_end * 1000).toLocaleDateString("de-DE")}</span></>
                )}
              </p>
              {activeSub.cancel_at_period_end && (
                <p className="mt-2 inline-flex items-center gap-2 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40">
                  <AlertCircle className="w-3 h-3" />
                  Wird zum Ende der Periode gekündigt
                </p>
              )}
            </div>
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="btn-green h-10 px-4 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-50"
              data-testid="billing-open-portal"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Im Stripe-Portal verwalten
            </button>
          </div>
        </div>
      ) : (
        <div className="nv-card mt-8 p-6 animate-slide-up" data-testid="billing-no-sub">
          <p className="label-eyebrow">Kein aktives Abo</p>
          <h2 className="font-display font-bold text-white text-xl mt-1">Du nutzt aktuell Prepaid-Minuten.</h2>
          <p className="text-sm text-novatus-text-2 mt-2 max-w-xl leading-relaxed">
            Mit einem Abonnement bekommst du jeden Monat ein festes Minuten-Kontingent zum
            günstigeren Preis. Jederzeit kündbar zum Periodenende.
          </p>
          <button
            onClick={openPaywall}
            className="btn-green h-10 px-4 rounded-lg inline-flex items-center gap-2 text-sm mt-4"
            data-testid="billing-open-paywall"
          >
            <Sparkles className="w-4 h-4" />
            Abo oder Minuten-Paket auswählen
          </button>
        </div>
      )}

      {/* Invoices table */}
      <div className="nv-card mt-6 p-6 animate-slide-up delay-1" data-testid="billing-invoices-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <p className="label-eyebrow flex items-center gap-2"><Receipt className="w-3.5 h-3.5 text-novatus-green" /> Rechnungen & Käufe</p>
            <p className="text-sm text-novatus-text-3 mt-1">
              Die letzten {invoices.length || 0} Transaktionen
              {hasStripeLink ? " · Stripe-Rechnungen mit PDF-Download" : ""}
            </p>
          </div>
          <button
            onClick={openPaywall}
            className="h-9 px-3 rounded-lg border border-novatus text-novatus-text-2 hover:bg-white/5 inline-flex items-center gap-1.5 text-xs font-bold"
            data-testid="billing-add-minutes"
          >
            <Plus className="w-3.5 h-3.5" /> Mehr Minuten kaufen
          </button>
        </div>

        {invoices.length === 0 ? (
          <div className="py-12 text-center text-novatus-text-3" data-testid="billing-empty-state">
            <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Noch keine Rechnungen vorhanden.</p>
            <p className="text-xs mt-1">Deine erste Bestellung erscheint hier nach erfolgreicher Bezahlung.</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-novatus-text-3 border-b border-novatus">
                  <th className="py-2 px-2 font-semibold">Datum</th>
                  <th className="py-2 px-2 font-semibold">Beschreibung</th>
                  <th className="py-2 px-2 font-semibold">Betrag</th>
                  <th className="py-2 px-2 font-semibold">Status</th>
                  <th className="py-2 px-2 font-semibold text-right">Aktion</th>
                </tr>
              </thead>
              <tbody className="text-novatus-text-2">
                {invoices.map((inv, i) => (
                  <tr key={inv.id || i} className="border-b border-novatus/60 hover:bg-white/[0.02]" data-testid={`billing-row-${i}`}>
                    <td className="py-3 px-2 whitespace-nowrap text-xs">{formatDate(inv.created_at)}</td>
                    <td className="py-3 px-2">
                      <span className="text-white text-sm font-medium">{inv.label}</span>
                      {inv.minutes ? (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-novatus-text-3">
                          <Timer className="w-3 h-3" /> +{inv.minutes} Min
                        </span>
                      ) : null}
                      {inv.number && (
                        <p className="text-[10px] text-novatus-text-3 mt-0.5 font-mono">{inv.number}</p>
                      )}
                    </td>
                    <td className="py-3 px-2 font-bold text-white whitespace-nowrap">{formatEUR(inv.amount_eur)}</td>
                    <td className="py-3 px-2">{statusBadge(inv.status)}</td>
                    <td className="py-3 px-2 text-right">
                      {inv.invoice_pdf ? (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-novatus-green hover:underline"
                          data-testid={`billing-pdf-${i}`}
                        ><Download className="w-3.5 h-3.5" /> PDF</a>
                      ) : inv.invoice_url ? (
                        <a
                          href={inv.invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-novatus-text-2 hover:text-novatus-green"
                          data-testid={`billing-hosted-${i}`}
                        ><ExternalLink className="w-3.5 h-3.5" /> Ansehen</a>
                      ) : (
                        <span className="text-[10px] text-novatus-text-3">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Self-service info */}
      <div className="nv-card mt-6 p-6 animate-slide-up delay-2" data-testid="billing-self-service">
        <p className="label-eyebrow text-novatus-green">Selbstverwaltung</p>
        <h3 className="font-display font-bold text-white text-lg mt-1">Im Stripe-Portal kannst du:</h3>
        <ul className="text-sm text-novatus-text-2 mt-3 space-y-1.5">
          <li>• Rechnungsadresse + Steuer-IdNr. (USt-IdNr.) hinzufügen oder ändern</li>
          <li>• Zahlungsart wechseln (Karte, SEPA, Apple/Google Pay)</li>
          <li>• Vergangene Rechnungen herunterladen</li>
          <li>• Abo kündigen oder upgraden</li>
        </ul>
        <button
          onClick={openPortal}
          disabled={portalLoading || !hasStripeLink}
          className="mt-4 h-10 px-4 rounded-lg border border-novatus text-novatus-text-2 hover:bg-white/5 inline-flex items-center gap-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="billing-open-portal-secondary"
          title={!hasStripeLink ? "Verfügbar nach deiner ersten Bestellung" : undefined}
        >
          {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Stripe-Portal öffnen
        </button>
        {!hasStripeLink && (
          <p className="text-[11px] text-novatus-text-3 mt-2">
            Verfügbar, sobald du mindestens eine erfolgreiche Bestellung hast.
          </p>
        )}
      </div>

      <p className="text-[11px] text-novatus-text-3 mt-8 text-center">
        Fragen zur Rechnung? Schreib uns an <a href="mailto:info@novatus-se.com" className="text-novatus-green hover:underline">info@novatus-se.com</a> oder schau in unsere{" "}
        <Link to="/agb" className="text-novatus-green hover:underline">AGB</Link>.
      </p>
    </div>
  );
};

export default BillingPage;
